import { getSdk } from "./sdkService";
import { config } from "../config/config";
import { getTimestamp } from "../utils/timeStamp";
import { v4 as uuidv4 } from "uuid";

const SIGNUP = "signup";
const LANDING = "landing";
const CREATE_ACCOUNT = "create-account";
const REDEEM_VOUCHER = "redeem-evoucher";
const THANK_YOU = "thank-you";
const LOGIN = "login";
const DETAIL = "detail";
const MOVIES = "movies";
const SERIES = "series";
const EPISODES = "episodes";
const CATALOGUE = "catalogue";
const BROWSE = "browse";
const GENRE = "genre";
const ALPHABETICAL = "alphabetical";
const LAST_ADDED = "last added";
const DISNEY = "disney";
const SEARCH = "search";
const SETTINGS = "settings";
const MY_LIBRARY = "mylibrary";
const LANGUAGE = "language";
const PROFILE = "profile";
const PARENTAL_CONTROL = "parental-control";
const PARENTAL_CONTROL_POPUP = "parentalcontrol-popup";
const ACCOUNT_STATUS = "accountstatus";
const DEVICES = "devices";
const CONTACT_US = "contactus";
const EVOUCHER = "evoucher";
const LOGOUT = "logout";
const EXIT = "exit";

/**
 * Collects page information and proceeds with its tracking. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.props).
 * @param {String} section - Section where the user is located.
 * @param {String} extraConfig - Possible extra data to be passed to retrieve analytics objects.
 */
export async function trackPage(section, extraConfig) {
  if (config.get().featureToggles.analytics) {
    const sdk = await getSdk();
    const sectionData = getSectionTrackingData.call(this, section, extraConfig)();
    sdk.analyticsManager.trackPage({
      path: `/${sectionData.pageName}`,
      ...sectionData
    });
  }
}

/**
 * Sets a custom dimension with the new parental level value.
 * @param {String} newParentalValue - New parental level value.
 * @public
 */
export async function setParentalValue(newParentalValue) {
  if (config.get().featureToggles.analytics) {
    const sdk = await getSdk();
    sdk.analyticsManager.setNewParentalValue(newParentalValue);
  }
}

/**
 * Sends a tracking event.
 * @param {String} category
 * @param {String} action
 * @param {String} label
 * @public
 */
export async function trackEvent(category, action, label) {
  if (config.get().featureToggles.analytics) {
    const sdk = await getSdk();
    sdk.analyticsManager.trackEvent({ category, action, label });
  }
}

/**
 * Returns analytics object related to the different sections pageviews.
 * @param {String} section - Section where the user is located.
 * @param {String} config - Possible extra data to be passed to retrieve analytics objects.
 * @returns {Function} Function that will retrieve an object containing pageview tracking object.
 * @private
 */
function getSectionTrackingData(section, config) {
  const pageviewSections = {
    landing: () => ({
      pageType: SIGNUP,
      pageName: `${SIGNUP}/${LANDING}`
    }),
    createAccount: () => ({
      pageType: SIGNUP,
      pageName: `${SIGNUP}/${CREATE_ACCOUNT}`
    }),
    redeemVoucher: () => ({
      pageType: SIGNUP,
      pageName: `${SIGNUP}/${REDEEM_VOUCHER}`
    }),
    thankYou: () => ({
      pageType: SIGNUP,
      pageName: `${SIGNUP}/${THANK_YOU}`
    }),
    login: () => ({
      pageType: LOGIN,
      pageName: LOGIN
    }),
    myLibrary: () => ({
      pageType: MY_LIBRARY,
      pageName: MY_LIBRARY
    }),
    parentalControl: () => ({
      pageType: `${SETTINGS}/${PARENTAL_CONTROL_POPUP}`,
      pageName: SETTINGS
    }),
    layout: () => getLayoutTrackingData.call(this),
    detail: () => getDetailPageTrackingData.call(this),
    episodeSelector: () => getEpisodeSelectorTrackingData.call(this),
    settings: () => getSettingsTrackingData.call(this),
    fullCatalog: () => getFullCatalogTrackingData.call(this),
    search: () => ({
      pageType: SEARCH,
      pageName: SEARCH
    })
  };

  return pageviewSections[section];
}

/**
 * Returns analytics object related to layout sections pageviews. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.state).
 * @returns {Object} Layout pageview tracking object.
 * @private
 */
function getLayoutTrackingData() {
  return { pageType: this.state.nameSection, pageName: this.state.nameSection };
}

/**
 * Returns analytics object related to detail page section pageview. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.props).
 * @returns {Object} Detail pageview tracking object.
 * @private
 */
function getDetailPageTrackingData() {
  const {
    name,
    match: {
      params: { titleId }
    },
    history: { location }
  } = this.props;
  const sectionName = (location.state && location.state.sectionAnalytics) || (name === "DetailMovie" ? MOVIES : SERIES);
  return {
    pageType: sectionName,
    pageName: `${sectionName}/${DETAIL}-${titleId}`
  };
}

/**
 * Returns analytics object related to episode selector section pageview. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.props).
 * @returns {Object} Episode selector pageview tracking object.
 * @private
 */
function getEpisodeSelectorTrackingData() {
  const {
    history: {
      location: {
        state: { seriesId, sectionName }
      }
    }
  } = this.props;
  return {
    pageType: sectionName,
    pageName: `${sectionName}/${DETAIL}-${seriesId}/${EPISODES}`
  };
}

/**
 * Returns analytics object related to settings section and subsections pageviews. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.state).
 * @returns {Object} Settings pageview tracking object.
 * @private
 */
function getSettingsTrackingData() {
  const subsectionsTrackingData = {
    language: LANGUAGE,
    profile: PROFILE,
    parentalControl: PARENTAL_CONTROL,
    accountStatus: ACCOUNT_STATUS,
    devices: DEVICES,
    contactUs: CONTACT_US,
    eVoucher: EVOUCHER,
    logout: LOGOUT,
    exit: EXIT
  };
  return {
    pageType: SETTINGS,
    pageName: `${SETTINGS}/${subsectionsTrackingData[this.state.activeSubsection]}`
  };
}

/**
 * Returns analytics object related to full catalog section pageview. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.sectionName).
 * @param {Object} genre - Selected genre on full catalog page.
 * @returns {Object} Full catalog pageview tracking object.
 * @private
 */
function getFullCatalogTrackingData() {
  const {
    history: { location }
  } = this.props;
  const sectionName = (location.state && location.state.sectionName) || this.sectionName;
  return {
    pageType: sectionName,
    pageName: `${sectionName}/${CATALOGUE}`
  };
}

/**
 * Gets full catalog selected genre data for event tracking pruposes. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.sectionName).
 * @param {String} genre - Genre name.
 * @returns {Object} Full catalog event tracking object.
 * @public
 */
export function getFullCatalogEventTrackingData(genre) {
  const sectionTrackingData = {
    alphabetical: { action: ALPHABETICAL, label: this.sectionName },
    lastAdded: { action: LAST_ADDED, label: this.sectionName }
  };
  const browseGenre =
    genre.tagTitleLocalized && genre.tagTitleLocalized.en && genre.tagTitleLocalized.en.toLowerCase() === DISNEY
      ? { action: `${BROWSE} ${DISNEY}`, label: MOVIES }
      : {
          action: `${BROWSE} ${GENRE}-${(genre.tagTitleLocalized && genre.tagTitleLocalized.en) || genre.tagTitle}`,
          label: `${MOVIES}`
        };
  return sectionTrackingData[genre.name] || browseGenre;
}

/**
 * Gets a string to pass it within the voucher redemption success tracking event data. It requires the
 * context of a React component wrapped in appContext to work (as it uses this.props).
 * @returns {String} String containing user payment and account status data.
 * @public
 */
export function getRedemptionSuccessTrackingData() {
  const {
    history: { location },
    applicationState: { userAccountStatus }
  } = this.props;
  const accountStatus = (location && location.state && location.state.fromSignup && "New") || userAccountStatus;
  const userPaymentMethod =
    this.userPayments &&
    this.userPayments.paymentMethods &&
    this.userPayments.paymentMethods.length &&
    this.userPayments.paymentMethods[0].paymentMethod;
  return `${accountStatus}-${userPaymentMethod || "none"}`;
}

/**
 * Sends user events.
 * @param {String} module_id - Module ID.
 * @param {String} id - Title ID.
 * @param {String} action - Event action.
 * @param {String} value - Event action value.
 * @param {String} eventType - Event type.
 * @public
 */
export async function sendUserEvent({ module_id, id, action, value, eventType = action, parent_id = false }) {
  const sdk = await getSdk();
  const eventData = {
    event_type: `media.${eventType}`,
    media_details: {
      action,
      media_id: id,
      ...(parent_id && { parent_id }),
      ...(value !== undefined && value !== null && { value: "" + value })
    }
  };
  module_id && Object.assign(eventData.media_details, { module_id });
  sdk.reportsManager.sendRecEngineEvent(eventData);
}

/**
 * Gets a layout related label to be sent among the event tracking data when a title within any module is clicked on.
 * Depending on the module category, it will return one label or another.
 * @param {String} category - Module category.
 * @param {Number} layoutOrder - Module order number within the whole layout.
 * @param {Number} moduleItemIndex - Index of the item selected within the module.
 * @param {String} moduleFriendlyTitle - Module friendly title.
 */
export async function getLayoutEventLabel(category, layoutOrder, moduleItemIndex, moduleFriendlyTitle) {
  const sdk = await getSdk();
  const { hero, editorial, myList, continueWatching } = sdk.mediaManager.carouselTypes;

  if (category === hero) {
    return `${moduleItemIndex + 1}`;
  } else if (category === editorial || category === continueWatching || category === myList) {
    return `${layoutOrder}|${moduleFriendlyTitle}`;
  }
  return `${layoutOrder}|${category}`;
}

/**
 * tracks the events in multiple pages regarding GTM datalayer
 */

async function getEventsCommonInitializeData(language, userName) {
  return {
    date_time: getTimestamp(),
    language,
    starz_uid: uuidv4()
  };
}

const eventsListRelatedPushData = {
  view_landingscreen: {
    event_step: "1"
  }
};


function getEventTrackingPushData(event) {
  return eventsListRelatedPushData[event];
}

export async function trackEventInitializeData({ page, language, userName, ...rest }) {
  if (typeof window.dataLayer !== "undefined") {
    const eventData = {
      event: "virtual_pageview",
      ...rest,
      ...(await getEventsCommonInitializeData(language, userName))
    };
    window.dataLayer.push(eventData);
  }
}

export function trackEventPushData({ event, label = "", ...rest }) {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event,
      event_action: event,
      event_label: label,
      ...rest,
      ...getEventTrackingPushData(event)
    });
  }
}
